/**
 * Portfolio summary cards showing key metrics across all holdings.
 * Displays Total NAV, Alternatives NAV, Unfunded Commitments, and Data Freshness.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";

interface PortfolioSummaryCardsProps {
  totalNav: number;
  navChange: number;
  altsNav: number;
  altsPct: number;
  unfundedCommitments: number;
  staleCount: number;
  pendingCount: number;
  onViewStale?: () => void;
}

export function PortfolioSummaryCards({
  totalNav,
  navChange,
  altsNav,
  altsPct,
  unfundedCommitments,
  staleCount,
  pendingCount,
  onViewStale,
}: PortfolioSummaryCardsProps) {
  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const hasDataIssues = staleCount > 0 || pendingCount > 0;

  return (
    <div className="grid gap-4 md:grid-cols-4">
      {/* Total NAV */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            Total NAV
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatCurrency(totalNav)}</div>
          <div className="flex items-center gap-1 text-xs mt-1">
            {navChange >= 0 ? (
              <TrendingUp className="h-3 w-3 text-green-600" />
            ) : (
              <TrendingDown className="h-3 w-3 text-red-600" />
            )}
            <span className={navChange >= 0 ? "text-green-600" : "text-red-600"}>
              {formatPercent(navChange)} QoQ
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Alternatives NAV */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            Alternatives
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatCurrency(altsNav)}</div>
          <p className="text-xs text-muted-foreground mt-1">
            {altsPct.toFixed(0)}% of portfolio
          </p>
        </CardContent>
      </Card>

      {/* Unfunded Commitments */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            Unfunded
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatCurrency(unfundedCommitments)}</div>
          <p className="text-xs text-muted-foreground mt-1">
            Remaining commitments
          </p>
        </CardContent>
      </Card>

      {/* Data Freshness */}
      <Card className={hasDataIssues ? "border-yellow-500/50 bg-yellow-50/30 dark:bg-yellow-900/10" : ""}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            {hasDataIssues && (
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
            )}
            Data Freshness
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasDataIssues ? (
            <>
              <div className="text-sm font-semibold text-yellow-700 dark:text-yellow-500">
                {staleCount > 0 && `${staleCount} stale`}
                {staleCount > 0 && pendingCount > 0 && ", "}
                {pendingCount > 0 && `${pendingCount} pending`}
              </div>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs mt-1"
                onClick={onViewStale}
              >
                View All Issues
              </Button>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold text-green-600">All current</div>
              <p className="text-xs text-muted-foreground mt-1">
                No stale data
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
